type EcommerceItem = {
  item_id: string;
  item_name: string;
  item_type: 'goods' | 'class';
  item_category: string;
  item_variant?: string;
  creator_id: string;
  creator_name: string;
  price: number;
  quantity: number;
};

type ViewItemPayload = {
  event: 'view_item';
  item_id: string;
  item_name: string;
  item_type: 'goods' | 'class';
  item_category: string;
  creator_name: string;
};

type BeginCheckoutPayload = {
  event: 'begin_checkout';
  value: number;
  currency: 'KRW';
  shipping: number;
  items: EcommerceItem[];
};

type PurchasePayload = {
  event: 'purchase';
  transaction_id: string;
  value: number;
  currency: 'KRW';
  shipping: number;
  payment_type: string;
  items: EcommerceItem[];
};

type DataLayerPayload =
  | ViewItemPayload
  | BeginCheckoutPayload
  | PurchasePayload;

// 결제 시작 시점에 결제 완료 페이지로 넘겨야 하는 GA4 purchase 이벤트 메타데이터.
// API trace 응답에 결제수단/배송비/항목별 단가가 없어 클라이언트 컨텍스트로 전달한다.
type PurchaseDataLayerContext = {
  shipping: number;
  payment_type: string;
  items: EcommerceItem[];
};

const PURCHASE_CONTEXT_STORAGE_KEY = 'gtm:purchase_context';

export const savePurchaseContext = (context: PurchaseDataLayerContext) => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(
      PURCHASE_CONTEXT_STORAGE_KEY,
      JSON.stringify(context)
    );
  } catch {
    // 저장 실패 시 silent. 완료 페이지에서 미존재로 처리됨
  }
};

export const consumePurchaseContext = (): PurchaseDataLayerContext | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PURCHASE_CONTEXT_STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PURCHASE_CONTEXT_STORAGE_KEY);
    return JSON.parse(raw) as PurchaseDataLayerContext;
  } catch {
    return null;
  }
};

// PaymentMethodType(KCP 결제수단 enum)을 GA4 payment_type 값으로 매핑.
// 매핑되지 않는 enum은 소문자로 변환해 그대로 전달한다.
export const toGtmPaymentType = (methodType: string): string => {
  switch (methodType) {
    case 'CARD':
    case 'CARD_GLOBAL':
      return 'card';
    case 'E_WALLET_KAKAOPAY':
      return 'kakaopay';
    case 'E_WALLET_NAVERPAY':
      return 'naverpay';
    case 'E_WALLET_TOSSPAY':
      return 'tosspay';
    case 'E_WALLET_SAMSUNGPAY':
      return 'samsungpay';
    case 'E_WALLET_PAYCO':
      return 'payco';
    case 'PHONE':
      return 'phone';
    case 'BANK':
      return 'bank';
    default:
      return methodType.toLowerCase();
  }
};

// dev phase 등 GTM 스크립트가 로드되지 않은 환경에서도 안전하게 호출 가능하도록
// window/dataLayer 가드 후 push. SSR 컨텍스트에서는 no-op.
export const pushDataLayer = (payload: DataLayerPayload) => {
  if (typeof window === 'undefined') return;
  try {
    const w = window as Window & { dataLayer?: unknown[] };
    w.dataLayer = w.dataLayer ?? [];
    w.dataLayer.push(payload);
  } catch {
    // GTM 미연동 또는 보안 정책 위반 시 silent fail
  }
};
